import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch } from 'lucide-react';
import { ICON_PX } from '../../ui';

/**
 * Friendly decision node — pill-shaped instead of diamond.
 * Practice managers see "Decision" with clear path labels.
 */
function GateNode({ data, selected }) {
  const { label, options = ['Yes', 'No'], status = 'pending', selectedOption } = data;
  const isAvailable = status === 'available';
  const isSatisfied = status === 'satisfied';

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full" />

      <div
        className={[
          'rounded-2xl border-2 px-5 py-3 min-w-[160px] text-center transition-all',
          isSatisfied ? 'bg-green-bg border-[var(--green)] shadow-sm' :
          isAvailable ? 'bg-[var(--aqua)]/5 border-[var(--aqua)] shadow-md' :
          'bg-white border-slate-200 shadow-sm',
          selected ? 'ring-2 ring-aqua ring-offset-2' : '',
          isAvailable ? 'cursor-pointer hover:shadow-lg' : '',
        ].join(' ')}
      >
        <div className="flex items-center justify-center gap-1.5 mb-1">
          <GitBranch size={ICON_PX.md} className={isSatisfied ? 'text-green-nn' : isAvailable ? 'text-[var(--aqua)]' : 'text-slate-400'} />
          <span className={`text-xs font-semibold uppercase tracking-wide ${isSatisfied ? 'text-green-nn' : isAvailable ? 'text-[var(--aqua)]' : 'text-slate-400'}`}>
            Decision
          </span>
        </div>
        <span className={`text-sm font-medium ${isSatisfied ? 'text-green-nn' : 'text-text-dark'}`}>
          {label || 'Choose a path'}
        </span>
      </div>

      {/* Output ports for each option */}
      {options.map((opt, i) => {
        const isLeft = i === 0;
        return (
          <Handle
            key={opt}
            type="source"
            position={isLeft ? Position.Left : Position.Right}
            id={opt.toLowerCase()}
            className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full"
          />
        );
      })}

      {/* Option labels below */}
      <div className="absolute -bottom-6 left-0 right-0 flex justify-between px-3">
        {options.map((opt) => (
          <span
            key={opt}
            className={[
              'text-[10px] font-medium rounded-full px-2 py-0.5',
              selectedOption === opt ? 'bg-[var(--aqua)]/10 text-[var(--aqua)] font-bold' : 'text-slate-400',
            ].join(' ')}
          >
            {opt}
          </span>
        ))}
      </div>
    </>
  );
}

export default memo(GateNode);
